"use client";

import type { StateCreator } from "zustand";
import type { DataBaseline } from "./session.slice";

export type ParticipantFlowStep =
  | "login"
  | "presurvey"
  | "session"
  | "postsurvey"
  | "completed";

export type ParticipantSurveyDraft = {
  pain: number;
  energy: number;
  positivity: number;
};

function createDefaultSurveyDraft(): ParticipantSurveyDraft {
  return {
    pain: 1,
    energy: 1,
    positivity: 1,
  };
}

export type ParticipantFlowSlice = {
  currentStep: ParticipantFlowStep;
  requestedReturnTo: string | null;
  preSurveyDraft: ParticipantSurveyDraft;
  postSurveyDraft: ParticipantSurveyDraft;
  baselineDraft: DataBaseline | null;
  lastCompletedSessionId: string | null;
  setCurrentStep: (step: ParticipantFlowStep) => void;
  setRequestedReturnTo: (value: string | null) => void;
  updatePreSurveyDraft: (patch: Partial<ParticipantSurveyDraft>) => void;
  resetPreSurveyDraft: () => void;
  updatePostSurveyDraft: (patch: Partial<ParticipantSurveyDraft>) => void;
  resetPostSurveyDraft: () => void;
  setBaselineDraft: (baseline: DataBaseline | null) => void;
  setLastCompletedSessionId: (sessionId: string | null) => void;
  resetParticipantFlow: () => void;
};

export const createParticipantFlowSlice: StateCreator<
  ParticipantFlowSlice,
  [],
  [],
  ParticipantFlowSlice
> = (set) => ({
  currentStep: "login",
  requestedReturnTo: null,
  preSurveyDraft: createDefaultSurveyDraft(),
  postSurveyDraft: createDefaultSurveyDraft(),
  baselineDraft: null,
  lastCompletedSessionId: null,
  setCurrentStep: (currentStep) => set({ currentStep }),
  setRequestedReturnTo: (requestedReturnTo) => set({ requestedReturnTo }),
  updatePreSurveyDraft: (patch) =>
    set((state) => ({
      preSurveyDraft: {
        ...state.preSurveyDraft,
        ...patch,
      },
    })),
  resetPreSurveyDraft: () => set({ preSurveyDraft: createDefaultSurveyDraft() }),
  updatePostSurveyDraft: (patch) =>
    set((state) => ({
      postSurveyDraft: {
        ...state.postSurveyDraft,
        ...patch,
      },
    })),
  resetPostSurveyDraft: () =>
    set({ postSurveyDraft: createDefaultSurveyDraft() }),
  setBaselineDraft: (baselineDraft) => set({ baselineDraft }),
  setLastCompletedSessionId: (lastCompletedSessionId) =>
    set({ lastCompletedSessionId }),
  resetParticipantFlow: () =>
    set({
      currentStep: "login",
      requestedReturnTo: null,
      preSurveyDraft: createDefaultSurveyDraft(),
      postSurveyDraft: createDefaultSurveyDraft(),
      baselineDraft: null,
      lastCompletedSessionId: null,
    }),
});
